import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Route, Router, Routes } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Message } from 'primeng/message';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { filter } from 'rxjs/internal/operators/filter';
import { take } from 'rxjs/internal/operators/take';
import { ImportsModule } from '../../../imports';
import { AuthGuard } from '../../core/guard/auth-guard';
import { DefaultData } from '../../core/model/default-data.model.cecopin';
import { User } from '../../core/model/user.model';
import { AuthService } from '../../core/service/auth/auth.service';
import { MenuRouter, UserService } from '../../core/service/auth/user.service';
import { BlogService } from '../../core/service/components-services/blog.service';
import { BreadcrumbService } from '../../core/service/components-services/breadscrumb.service';
import { CalypsoService } from '../../core/service/components-services/calypso.service';
import {
  ColsTranslate,
  ColsTranslateService,
} from '../../core/service/components-services/cols-translate.service';
import { CookiesService } from '../../core/service/components-services/Cookies.service';
import { ConfigService } from '../../core/service/config/config.service';
import { ToastMessageService } from '../../core/service/messages/toastmessage.service';
import { LAZY_COMPONENTS } from '../../routes.map';
import { ReloginScreenComponent } from './reloginscreen.component';
import { PasswordChangeGuard } from '../../core/guard/passwordchange-guard';
import { ChangePasswordComponent } from '../administration/change-password/change-password.component';

@Component({
  template: '',
  imports: [FormsModule, ImportsModule],
})
export abstract class LoginBaseComponent implements OnInit, OnDestroy {
  // ─── UI State ───────────────────────────────
  username = '';
  password = '';
  isLogging = false;
  isReloging = false;
  loading = false;
  Fixing = false;
  msgs: Message[] = [];
  msg: Message;
  LogoReloging = '';
  displayFixLogin = false;
  documentUrl = '';
  captchaToken = '';
  sitekey = '';
  skipcatcha: boolean = false;
  name = '';
  customer = '';
  iscecopin = false;
  // ─── 2FA State ──────────────────────────────
  LoginStepOne = true;
  LoginStepTwo = false;
  codeSlot1 = '';
  codeSlot2 = '';
  codeSlot3 = '';
  codeSlot4 = '';
  codeSlot5 = '';
  codeSlot6 = '';
  totalTime = 10 * 60 * 1000;
  timeLeft = this.totalTime;
  progress = 100;
  intervalId: any;

  translate = inject(TranslateService);

  @ViewChild('reloginScreen') reloginScreen: ReloginScreenComponent;

  constructor(
    private configService: ConfigService,
    private router: Router,
    private authService: AuthService,
    private cookiesService: CookiesService,
    private userService: UserService,
    private calypsoService: CalypsoService,
    private colsTranslateService: ColsTranslateService,
    private messageService: ToastMessageService,
    private breadscrumbService: BreadcrumbService,
    private blogService: BlogService,
    private activeRoute: ActivatedRoute,
  ) {
    this.customer = this.configService.get('name') as string;
    this.sitekey = this.configService.get('captcha') as string;
    this.skipcatcha = this.configService.get('skipcatcha') as boolean;
    this.documentUrl = this.configService.get('documentUrl') + 'Manual_Login.pdf';
    this.name = this.configService.get('name') as string;
    this.iscecopin = this.configService.get('cecopin') as boolean;
  }

  // ─────────────────────────────────────────────
  // LIFECYCLE
  // ─────────────────────────────────────────────

  ngOnInit(): void {
    this.setLogo();
    const { token } = this.cookiesService.parseCookies();
    const routeElements = this.router.url.split('/');
    const hasQueryParams = routeElements[1].includes('?');

    if (hasQueryParams) {
      const params = new URLSearchParams(routeElements[1].split('?')[1]);
      const urlId = params.get('u');
      const urlToken = params.get('k');
      if (token) this.relogin(token);
      this.dashboardLogin(urlId, urlToken);
      return;
    }

    if (token) this.relogin(token);
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalId);
  }

  // ─────────────────────────────────────────────
  // HELPERS PRIVADOS
  // ─────────────────────────────────────────────

  private setLogo(): void {
    this.LogoReloging =
      this.name === 'GISECloud STL'
        ? 'assets/img/policia_local_inicio.png'
        : 'assets/img/logo-GISECloud-vertical-dark.png';
  }

  private setLoadingState(value: boolean): void {
    this.isReloging = value;
    this.isLogging = value;
  }

  private async loadDefaultData(): Promise<void> {
    const data = await this.defaultDataByUser();
    DefaultData.municipalityId = data['municipalityIneCode'];
    DefaultData.municipalityName = data['municipalityName'];
    DefaultData.islandId = data['islandIneCode'];
    DefaultData.provinceId = data['provinceIneCode'];
    this.calypsoService.onConnect.emit();
  }

  private async loadColsTranslate(): Promise<void> {
    try {
      const cols = (await this.colsTranslateService.getAllTranslations()) as ColsTranslate[];
      this.colsTranslateService.dataColsTranslate = cols;
    } catch (error) {
      console.error('Error cargando columnas de traducción:', error);
    }
  }

  private saveTokenCookies(user: User): void {
    this.cookiesService.setCookie(
      'token',
      user.lastToken,
      new Date(user.tokenexpiration).toString(),
    );
    this.cookiesService.setCookie(
      'refreshtoken',
      user.refreshToken,
      new Date(user.refreshTokenExpiration).toString(),
    );
  }

  private async tryLoadApiConexiones(): Promise<void> {
    try {
      const api = await this.authService.loginApiConexionesExternas();
      this.cookiesService.saveApiContextToken(api);
    } catch (error) {
      console.error('Error obteniendo token de API externa:', error);
    }
  }

  // ─────────────────────────────────────────────
  // NAVEGACIÓN POST-LOGIN
  // ─────────────────────────────────────────────

  private navigateAfterLogin(user: User, useSavedUrl = false): void {
    this.authService.loggedIn = true;
    this.authService.passwordchanged = user.passwordchanged;
    this.authService.onLogIn.emit(user);
   

    // Quitar spinner solo cuando el lazy module haya cargado
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => this.setLoadingState(false));

    let iscecopin = this.configService.get('cecopin') as boolean;

    useSavedUrl
      ? this.navigateToURlSaved(user.email)
      : iscecopin
        ? this.router.navigate(['/cecopin/incidentes-diarios'])
        : this.router.navigate(['/inicio']);
  }

  // ─────────────────────────────────────────────
  // FLUJO COMÚN POST-LOGIN OK
  // ─────────────────────────────────────────────

  private async handleLoginSuccess(user: User, useSavedUrl = false): Promise<void> {
    this.userService.currentUser = user;

    // await this.loadDefaultData();
    await this.waitForView();
    await new Promise((r) => setTimeout(r, 400));
    this.reloginScreen?.nextStep();

    if (this.name === 'GISECloud La Oliva') {
      await this.tryLoadApiConexiones();
    }

    await new Promise((r) => setTimeout(r, 200));
    this.reloginScreen?.nextStep();
    this.saveTokenCookies(user);
    await this.generateRouter();

    await new Promise((r) => setTimeout(r, 200));
    this.reloginScreen?.nextStep();
    await this.loadColsTranslate();

    this.reloginScreen?.completeAll(); // → ¡completado!
    await new Promise((r) => setTimeout(r, 200));
    this.navigateAfterLogin(user, useSavedUrl);
  }

  private waitForView(): Promise<void> {
    return new Promise((resolve) => {
      if (this.reloginScreen) {
        resolve();
        return;
      }
      const check = setInterval(() => {
        if (this.reloginScreen) {
          clearInterval(check);
          resolve();
        }
      }, 50);
    });
  }

  // ─────────────────────────────────────────────
  // RELOGIN
  // ─────────────────────────────────────────────

  private relogin(token: string): void {
    this.setLoadingState(true);

    this.authService.ReloginSocket(token).subscribe({
      next: async (response: any) => {
        if (response?.['msg'] === 'Ok') {
          await this.handleLoginSuccess(response['data'] as User, true);
        } else if (response?.['msg'] === 'No') {
          this.setLoadingState(false);
          this.msgs = [];
          this.messageService.showMessage('global', 'warn', ' ', response['error'], 0);
          this.cookiesService.deleteTokenAndExpire();
        }
      },
      error: (error) => {
        console.error(error);
        this.msgs = [];
        this.setLoadingState(false);
        this.messageService.showMessage('global', 'warn', 'Info', 'No se ha podido relogear', 0);
      },
    });
  }

  // ─────────────────────────────────────────────
  // DASHBOARD LOGIN
  // ─────────────────────────────────────────────

  private dashboardLogin(urlId: string, urlToken: string): void {
    this.setLoadingState(true);

    this.authService.DashboardLogin(urlId, urlToken).subscribe({
      next: async (response: any) => {
        if (response?.['msg'] === 'No') {
          this.messageService.showMessage('global', 'warn', ' ', response['error'], 0);
          this.setLoadingState(false);
        }

        if (response?.['msg'] === 'Step1') {
          this.LoginStepOne = false;
          this.LoginStepTwo = true;
          this.startCountdown();
          this.setLoadingState(false);
        }

        if (response?.['msg'] === 'Ok') {
          await this.handleLoginSuccess(response['data'] as User);
        }
      },
      error: (error) => {
        console.error(error);
        this.setLoadingState(false);
      },
    });
  }

  // ─────────────────────────────────────────────
  // LOGIN 2FA - STEP 1
  // ─────────────────────────────────────────────

  Login2FAStepOne(): void {
    this.setLoadingState(true);

    this.authService.Login2AFStepOne(this.username, this.password, this.captchaToken).subscribe({
      next: async (response: any) => {
        if (response?.['msg'] === 'No') {
          this.msgs = [];
          this.messageService.showMessage('global', 'warn', ' ', response['error'], 0);
          this.setLoadingState(false);
        }

        if (response?.['msg'] === 'Step1') {
          this.LoginStepOne = false;
          this.LoginStepTwo = true;
          this.startCountdown();
          this.setLoadingState(false);
        }

        if (response?.['msg'] === 'Ok') {
          await this.handleLoginSuccess(response['data'] as User);
        }
      },
      error: (error) => {
        console.error(error);
        this.messageService.showMessage('global', 'warn', 'Info:', 'No se pudo iniciar sesión.', 0);
        this.setLoadingState(false);
      },
    });
  }

  // ─────────────────────────────────────────────
  // LOGIN 2FA - STEP 2
  // ─────────────────────────────────────────────

  Login2FAStepTwo(): void {
    const code = `${this.codeSlot1}${this.codeSlot2}${this.codeSlot3}${this.codeSlot4}${this.codeSlot5}${this.codeSlot6}`;

    this.authService.Login2AFStepTwo(this.username, this.password, code).subscribe({
      next: async (response: any) => {
        if (response?.['msg'] === 'No') {
          this.msgs = [];
          this.messageService.showMessage('global', 'warn', ' ', response['error'], 0);
          this.setLoadingState(false);
        }

        if (response?.['msg'] === 'Ok') {
          this.setLoadingState(true);
          this.LoginStepOne = true;
          this.LoginStepTwo = false;
          await this.handleLoginSuccess(response['data'] as User);
        }
      },
      error: (error) => {
        console.error(error);
        this.setLoadingState(false);
      },
    });
  }

  // ─────────────────────────────────────────────
  // RESTO DE MÉTODOS PÚBLICOS
  // ─────────────────────────────────────────────

  loginOnKeyEnter(): void {
    this.Login2FAStepOne();
  }

  checkDisabledBtnSendCode(): boolean {
    return [
      this.codeSlot1,
      this.codeSlot2,
      this.codeSlot3,
      this.codeSlot4,
      this.codeSlot5,
      this.codeSlot6,
    ].some((slot) => slot === '');
  }

  startCountdown(): void {
    this.intervalId = setInterval(() => {
      this.timeLeft -= 1000;
      this.progress = (this.timeLeft / this.totalTime) * 100;
      if (this.timeLeft <= 0) {
        clearInterval(this.intervalId);
        this.resetLoginState();
      }
    }, 1000);
  }

  resetLoginState(): void {
    this.LoginStepOne = true;
    this.LoginStepTwo = false;
    this.timeLeft = this.totalTime;
    this.progress = 100;
  }

  moveToNext(event: Event, currentInput: string, nextInput: string | null): void {
    const input = event.target as HTMLInputElement;
    if (input.value.length === 1 && nextInput) {
      document.getElementById(nextInput)?.focus();
    }
  }

  navigateToURlSaved(user: string): void {
    const url = this.cookiesService.getLastUrl(user);
    this.router.navigateByUrl(url);
  }

  goToPasswordRecovery(): void {
    this.router.navigate(['recuperar-contrasena'])
  }

  mostrarManual(): void {
    window.open(this.documentUrl);
  }

  CleanLocalStorage(): void {
    this.cookiesService.deleteTokenAndExpire();
  }

  FixLogin(): void {
    this.Fixing = true;
    const { token } = this.cookiesService.parseCookies();

    this.authService.logoutLogginSession(token).subscribe({
      next: () => {
        this.authService.logout();
        this.cookiesService.deleteTokenAndExpire();
        this.msgs = [];
        this.displayFixLogin = false;
        this.Fixing = false;
        this.messageService.showMessage(
          'global',
          'success',
          'Éxito',
          'Sesión solucionada, inicie sesión con usuario y contraseña',
          0,
        );
      },
      error: () => {
        const { token: currentToken } = this.cookiesService.parseCookies();
        const msg = currentToken
          ? 'No se ha podido reparar la sesión. Cierre y por favor, contacte con soporte técnico'
          : 'No se ha podido reparar la sesión. No se encuentra el token, contacte al soporte técnico';
        this.msgs = [];
        this.messageService.showMessage('global', 'warn', 'Advertencia', msg, 0);
        this.displayFixLogin = false;
        this.Fixing = false;
      },
      complete: () => {
        this.displayFixLogin = false;
      },
    });
  }

  async defaultDataByUser(): Promise<any> {
    return this.userService.getDefaultDataByUserId(this.userService.currentUser.id).toPromise();
  }

  onCaptchaResolved(token: string): void {
    this.captchaToken = token;
  }

  async generateRouter() {
    try {
      let res = await firstValueFrom(
        this.userService.getMenuRouter(this.configService.get('cecopin') as boolean),
      );
      var changePassword = {
          path: 'cambio-contrasena',
          canActivate: [AuthGuard],
          component: ChangePasswordComponent
        };
      let routes = await this.formatRoute(res as MenuRouter[]);
      const allRoutes = [...this.router.config, ...routes,changePassword];
      this.router.resetConfig(allRoutes);

    } catch (error) {
      console.log(error);
    }
  }

  private async formatRoute(items: MenuRouter[]): Promise<Routes> {
    return Promise.all(
      items.map(async (i) => {
        const hasChildren = i.childrens && i.childrens.length > 0;

        //─── Rutas con redirectTo: SIN canActivate ───────────────────────
        if (!i.componentpath && !hasChildren) {
          console.warn(`[Router] Route "${i.router}" has no component or children, skipping.`);
          return {
            path: i.router,
            redirectTo: '/',
            pathMatch: 'full',
          };
        }

        const route: Route = {
          path: i.router,
          canActivate: [AuthGuard,PasswordChangeGuard],
          data: { icon: i.icon, i18n: i.i18n, visible: i.isvisible, name: i.label },
        };

        if (i.componentpath) {
          const loader = LAZY_COMPONENTS[i.componentpath];
          if (!loader) {
            console.warn(`[Router] No lazy component found for key: "${i.componentpath}"`);
          }
          route.loadComponent = loader;
        }
        if (i.childrens && i.childrens.length > 0) {
          route.canActivateChild = [AuthGuard,PasswordChangeGuard];
          route.children = [
            ...(await this.formatRoute(i.childrens)),
            {
              path: '',
              redirectTo: i.childrens[0].router,
              pathMatch: 'full',
            },
          ];
        }

        return route;
      }),
    );
  }
}
